import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { estimateApi } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import {
  Search, MapPin, User, Wrench, CalendarClock, Clock, FileText,
  ChevronDown, ChevronUp, AlertTriangle, Euro, MessageSquare, FileCheck, TimerReset
} from 'lucide-react';

const KPI_GROUPS = [
  { key: 'in_lavorazione', label: 'In lavorazione', statuses: ['nuovo', 'contattato', 'non_risponde', 'assegnato', 'in_preventivazione', 'sopralluogo_effettuato'] },
  { key: 'appuntamento_fissato', label: 'Appuntamento fissato', statuses: ['appuntamento_fissato'] },
  { key: 'preventivo_presentato', label: 'Presentati', statuses: ['preventivo_presentato'] },
  { key: 'in_trattativa', label: 'In trattativa', statuses: ['in_trattativa'] },
  { key: 'in_contratto', label: 'In contratto', statuses: ['in_contratto'] },
  { key: 'convertito', label: 'Convertiti', statuses: ['convertito'] },
  { key: 'perso', label: 'Persi', statuses: ['perso'] },
];

// KPI bar operativa (stile Projects): no stati terminali, + bucket temporali. Pipeline = metrica.
const KPI_BAR = [
  { key: 'in_lavorazione', label: 'In lavorazione', icon: Clock, color: '#FF7819', tint: '#FFF0E5', statuses: ['nuovo', 'contattato', 'non_risponde', 'assegnato', 'in_preventivazione', 'sopralluogo_effettuato'] },
  { key: 'appuntamento_fissato', label: 'Appuntamento fissato', icon: CalendarClock, color: '#2B6CB0', tint: '#E5F4FF', statuses: ['appuntamento_fissato'] },
  { key: 'preventivo_presentato', label: 'Presentati', icon: FileText, color: '#4F46E5', tint: '#EEF0FF', statuses: ['preventivo_presentato'] },
  { key: 'in_trattativa', label: 'In trattativa', icon: MessageSquare, color: '#9333EA', tint: '#F7EEFF', statuses: ['in_trattativa'] },
  { key: 'in_contratto', label: 'In contratto', icon: FileCheck, color: '#008A3D', tint: '#E5F7ED', statuses: ['in_contratto'] },
  { key: 'bucket_da_verificare', label: 'Fermi 15–30 gg', tooltip: 'Nessuna attività commerciale significativa da 15–30 giorni.', icon: AlertTriangle, color: '#B45309', tint: '#FEF3E2', bucket: 'da_verificare' },
  { key: 'bucket_fermi', label: 'Fermi >30 gg', tooltip: 'Nessuna attività commerciale significativa da oltre 30 giorni.', icon: TimerReset, color: '#E05252', tint: '#FDECEC', bucket: 'fermi' },
];

const BUCKETS = [
  { key: 'recenti', title: 'Recenti', desc: 'Attività commerciale negli ultimi 7 giorni', ring: '', dot: 'bg-emerald-500' },
  { key: 'da_seguire', title: 'Da seguire', desc: 'Nessuna attività significativa da 8–14 giorni', ring: '', dot: 'bg-amber-300' },
  { key: 'da_verificare', title: 'Fermi da 15 a 30 giorni', desc: 'Nessuna attività commerciale significativa da 15–30 giorni.', ring: '', dot: 'bg-amber-500' },
  { key: 'fermi', title: 'Fermi da oltre 30 giorni', desc: 'Richiedono un aggiornamento commerciale.', ring: '', dot: 'bg-orange-600' },
];

const STATUS_BADGE = {
  nuovo: 'bg-slate-100 text-slate-700', contattato: 'bg-slate-100 text-slate-700',
  non_risponde: 'bg-slate-100 text-slate-700', assegnato: 'bg-blue-100 text-blue-700',
  appuntamento_fissato: 'bg-indigo-100 text-indigo-700', sopralluogo_effettuato: 'bg-indigo-100 text-indigo-700',
  in_preventivazione: 'bg-cyan-100 text-cyan-700', preventivo_presentato: 'bg-purple-100 text-purple-700',
  in_trattativa: 'bg-amber-100 text-amber-800', in_contratto: 'bg-emerald-100 text-emerald-700',
  convertito: 'bg-green-100 text-green-700', perso: 'bg-red-100 text-red-700',
};

const relDays = (d) => {
  if (d === null || d === undefined) return '—';
  if (d <= 0) return 'Oggi';
  if (d === 1) return 'Ieri';
  return `${d} giorni fa`;
};

const fmtAppt = (iso) => {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${mm} · ${hh}:${mi}`;
};

const OpportunityCard = ({ o, onOpen }) => {
  const warnAppt = !o.next_appointment && ['da_verificare', 'fermi'].includes(o.bucket);
  return (
    <div
      role="button" tabIndex={0}
      onClick={() => onOpen(o.lead_id)}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); onOpen(o.lead_id); } }}
      className="group rounded-2xl border border-[#E4E4E4] bg-white p-4 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-[#D4D4D4] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#FF7819]"
      data-testid={`opportunity-card-${o.lead_id}`}
    >
      {/* HEADER */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-base font-semibold text-[#171717] truncate">{o.client_name}</h3>
          {(o.effective_site_address || o.site_address_unavailable) && (
            <div className="flex items-center gap-1 mt-0.5 text-sm text-[#737373]">
              <MapPin className="w-3.5 h-3.5 shrink-0" />
              <span className={`truncate ${o.site_address_unavailable ? 'text-amber-600 italic' : ''}`} data-testid={`opportunity-address-${o.lead_id}`}>
                {o.site_address_unavailable ? 'Indirizzo cantiere non disponibile' : o.effective_site_address}
              </span>
            </div>
          )}
        </div>
        <span className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] font-medium ${STATUS_BADGE[o.status] || 'bg-slate-100 text-slate-700'}`} data-testid={`opportunity-status-${o.lead_id}`}>
          {o.status_label}
        </span>
      </div>

      <div className="my-3 border-t border-[#F0F0F0]" />

      {/* VALUE + ACTIVITY */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#A3A3A3] font-medium">Valore</p>
          {o.estimate_count > 1 ? (
            <>
              <p className="text-lg font-bold text-[#171717] mt-0.5 leading-tight" data-testid={`opportunity-value-${o.lead_id}`}>Da {formatCurrency(o.min_value)}</p>
              <p className="text-xs text-[#737373] mt-0.5">{o.estimate_count} preventivi · fino a {formatCurrency(o.max_value)}</p>
            </>
          ) : (
            <>
              <p className="text-lg font-bold text-[#171717] mt-0.5 leading-tight" data-testid={`opportunity-value-${o.lead_id}`}>{formatCurrency(o.min_value)}</p>
              <p className="text-xs text-[#737373] mt-0.5">1 preventivo</p>
            </>
          )}
        </div>
        <div>
          <p className="text-[11px] uppercase tracking-wide text-[#A3A3A3] font-medium">Ultima attività</p>
          <p className="text-sm font-semibold text-[#525252] mt-0.5 inline-flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-[#A3A3A3]" />{relDays(o.days_since_activity)}
          </p>
          {o.current_estimate_number && (
            <p className="text-[11px] text-[#A3A3A3] mt-1 truncate inline-flex items-center gap-1"><FileText className="w-3 h-3" />{o.current_estimate_number}</p>
          )}
        </div>
      </div>

      {/* APPOINTMENT */}
      <div className="mt-3">
        {o.next_appointment ? (
          <div className="rounded-lg bg-[#F0F7FF] border border-[#CFE3FB] px-2.5 py-1.5" data-testid={`opportunity-appt-${o.lead_id}`}>
            <p className="text-[11px] uppercase tracking-wide text-[#2B6CB0] font-medium">Appuntamento</p>
            <p className="text-sm font-semibold text-[#1E4E8C]">{fmtAppt(o.next_appointment.date)} · {o.next_appointment.type}</p>
          </div>
        ) : (
          <p className={`text-xs inline-flex items-center gap-1.5 ${warnAppt ? 'text-amber-600' : 'text-[#A3A3A3]'}`} data-testid={`opportunity-no-appt-${o.lead_id}`}>
            {warnAppt && <AlertTriangle className="w-3.5 h-3.5" />}Nessun appuntamento programmato
          </p>
        )}
      </div>

      <div className="my-3 border-t border-[#F0F0F0]" />

      {/* FOOTER PM + TECNICO */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-[#525252]">
        <span className="inline-flex items-center gap-1.5">
          <User className="w-4 h-4 text-[#A3A3A3]" /> <span className="text-[#A3A3A3]">PM</span> {o.pm_name || '—'}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Wrench className="w-4 h-4 text-[#A3A3A3]" /> <span className="text-[#A3A3A3]">Tecnico</span> {o.technician_name || '—'}
        </span>
      </div>
    </div>
  );
};

const EstimatesPage = () => {
  const navigate = useNavigate();
  const [opps, setOpps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [techFilter, setTechFilter] = useState('all');
  const [pmFilter, setPmFilter] = useState('all');
  const [apptFilter, setApptFilter] = useState('all');
  const [sortBy, setSortBy] = useState('recent');
  const [visible, setVisible] = useState({ recenti: 4, da_seguire: 4, da_verificare: 4, fermi: 4 });

  useEffect(() => {
    (async () => {
      try {
        const res = await estimateApi.getOpportunities();
        setOpps(res.data.opportunities || []);
      } catch (e) {
        toast.error('Errore nel caricamento opportunità');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const techOptions = useMemo(() => Array.from(new Set(opps.map(o => o.technician_name).filter(Boolean))).sort(), [opps]);
  const pmOptions = useMemo(() => Array.from(new Set(opps.map(o => o.pm_name).filter(Boolean))).sort(), [opps]);

  const kpis = useMemo(() => {
    const counts = {};
    KPI_BAR.forEach(k => {
      counts[k.key] = k.bucket
        ? opps.filter(o => !o.is_terminal && o.bucket === k.bucket).length
        : opps.filter(o => k.statuses.includes(o.status)).length;
    });
    const pipeline = opps.filter(o => !o.is_terminal).reduce((s, o) => s + (o.min_value || 0), 0);
    return { counts, pipeline };
  }, [opps]);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return opps.filter(o => {
      const matchSearch = !s ||
        o.client_name?.toLowerCase().includes(s) ||
        o.effective_site_address?.toLowerCase().includes(s) ||
        o.current_estimate_number?.toLowerCase().includes(s);
      const group = KPI_GROUPS.find(g => g.key === statusFilter);
      const barBucket = statusFilter === 'bucket_da_verificare' ? 'da_verificare' : (statusFilter === 'bucket_fermi' ? 'fermi' : null);
      let matchStatus = true;
      if (group) matchStatus = group.statuses.includes(o.status);
      else if (barBucket) matchStatus = !o.is_terminal && o.bucket === barBucket;
      const matchTech = techFilter === 'all' || o.technician_name === techFilter;
      const matchPm = pmFilter === 'all' || o.pm_name === pmFilter;
      const matchAppt = apptFilter === 'all' || (apptFilter === 'with' ? !!o.next_appointment : !o.next_appointment);
      return matchSearch && matchStatus && matchTech && matchPm && matchAppt;
    });
  }, [opps, search, statusFilter, techFilter, pmFilter, apptFilter]);

  const sortFn = (a, b) => {
    switch (sortBy) {
      case 'stale': return (b.days_since_activity ?? -1) - (a.days_since_activity ?? -1);
      case 'value_high': return (b.min_value || 0) - (a.min_value || 0);
      case 'value_low': return (a.min_value || 0) - (b.min_value || 0);
      case 'client': return a.client_name.localeCompare(b.client_name);
      case 'appointment': {
        const ad = a.next_appointment ? new Date(a.next_appointment.date).getTime() : Infinity;
        const bd = b.next_appointment ? new Date(b.next_appointment.date).getTime() : Infinity;
        return ad - bd;
      }
      default: return (a.days_since_activity ?? Infinity) - (b.days_since_activity ?? Infinity); // recent
    }
  };

  const grouped = useMemo(() => {
    const g = { recenti: [], da_seguire: [], da_verificare: [], fermi: [] };
    filtered.filter(o => !o.is_terminal && o.bucket).forEach(o => { if (g[o.bucket]) g[o.bucket].push(o); });
    Object.keys(g).forEach(k => g[k].sort(sortFn));
    return g;
  }, [filtered, sortBy]);

  const flatList = useMemo(() => [...filtered].sort(sortFn), [filtered, sortBy]);
  const showBuckets = statusFilter === 'all';

  if (loading) {
    return <div className="flex items-center justify-center py-24"><div className="animate-spin w-8 h-8 border-2 border-[#FF7819] border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="estimates-page">
      <div>
        <h1 className="text-2xl font-bold text-[#171717]">Preventivi</h1>
        <p className="text-sm text-[#737373]">Controllo operativo delle opportunità commerciali</p>
      </div>

      {/* KPI — stesso design system delle Project Card. Desktop 4×2 (riga1 pipeline commerciale, riga2 chiusura/attenzione) */}
      <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        {KPI_BAR.map(k => {
          const Icon = k.icon;
          const active = statusFilter === k.key;
          return (
            <button
              key={k.key} type="button"
              onClick={() => setStatusFilter(active ? 'all' : k.key)}
              data-testid={`kpi-${k.key}`}
              title={k.tooltip || k.label}
              className={`text-left rounded-xl border bg-white px-3 py-2.5 transition-all duration-200 hover:shadow-md ${active ? '' : 'border-[#E4E4E4]'}`}
              style={active ? { borderColor: k.color, boxShadow: `0 0 0 1px ${k.color}` } : undefined}
            >
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: k.tint }}>
                  <Icon className="w-4 h-4" style={{ color: k.color }} />
                </div>
                <div className="min-w-0">
                  <p className="text-xl font-semibold text-[#3C3C3C] leading-none">{kpis.counts[k.key]}</p>
                  <p className="text-[11px] text-[#737373] mt-1 leading-tight">{k.label}</p>
                </div>
              </div>
            </button>
          );
        })}
        {/* Metrica pura: nessun quick-filter, nessun ring */}
        <div className="rounded-xl border border-[#E4E4E4] bg-white px-3 py-2.5" data-testid="kpi-pipeline">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 bg-[#E5F4FF] rounded-lg flex items-center justify-center shrink-0">
              <Euro className="w-4 h-4 text-[#0078C8]" />
            </div>
            <div className="min-w-0">
              <p className="text-base sm:text-lg font-semibold text-[#3C3C3C] leading-none" title="Valore prudenziale calcolato sul preventivo più basso di ciascuna opportunità">{formatCurrency(kpis.pipeline)}</p>
              <p className="text-[11px] text-[#737373] mt-1 leading-tight">Valore pipeline</p>
            </div>
          </div>
        </div>
      </div>

      {/* TOOLBAR responsive: search full-width + filtri in grid ordinata */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3A3A3]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca cliente, indirizzo cantiere, n° preventivo..." className="pl-9" data-testid="estimates-search" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2">
        <Select value={KPI_GROUPS.find(g => g.key === statusFilter) ? statusFilter : 'all'} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full" data-testid="filter-status"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            {KPI_GROUPS.map(g => <SelectItem key={g.key} value={g.key}>{g.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={techFilter} onValueChange={setTechFilter}>
          <SelectTrigger className="w-full" data-testid="filter-tech"><SelectValue placeholder="Tecnico" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tecnici</SelectItem>
            {techOptions.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="w-full" data-testid="filter-pm"><SelectValue placeholder="PM" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i PM</SelectItem>
            {pmOptions.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={apptFilter} onValueChange={setApptFilter}>
          <SelectTrigger className="w-full" data-testid="filter-appt"><SelectValue placeholder="Appuntamento" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti</SelectItem>
            <SelectItem value="with">Con appuntamento</SelectItem>
            <SelectItem value="without">Senza appuntamento</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sortBy} onValueChange={setSortBy}>
          <SelectTrigger className="w-full" data-testid="filter-sort"><SelectValue placeholder="Ordina" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="recent">Più recenti</SelectItem>
            <SelectItem value="stale">Fermi da più tempo</SelectItem>
            <SelectItem value="value_high">Valore più alto</SelectItem>
            <SelectItem value="value_low">Valore più basso</SelectItem>
            <SelectItem value="client">Cliente</SelectItem>
            <SelectItem value="appointment">Prossimo appuntamento</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>

      {/* BODY */}
      {showBuckets ? (
        <div className="space-y-8">
          {BUCKETS.map(b => {
            const list = grouped[b.key];
            if (!list.length) return null;
            const shown = visible[b.key];
            const total = list.reduce((s, o) => s + (o.min_value || 0), 0);
            return (
              <section key={b.key} data-testid={`bucket-${b.key}`}>
                <div className="flex items-start gap-2 mb-3">
                  <span className={`w-2.5 h-2.5 rounded-full mt-1.5 ${b.dot}`} />
                  <div>
                    <h2 className="text-lg font-semibold text-[#171717]">{b.title} <span className="text-[#A3A3A3] font-normal text-sm">· {list.length} opportunità · {formatCurrency(total)}</span></h2>
                    <p className="text-xs text-[#A3A3A3]">{b.desc}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
                  {list.slice(0, shown).map(o => <OpportunityCard key={o.lead_id} o={o} onOpen={(id) => navigate(`/leads/${id}`)} />)}
                </div>
                {list.length > 4 && (
                  <div className="mt-3 flex items-center gap-3 text-sm">
                    <span className="text-[#A3A3A3]">{Math.min(shown, list.length)} di {list.length}</span>
                    {shown < list.length && (
                      <button data-testid={`show-more-${b.key}`} onClick={() => setVisible(v => ({ ...v, [b.key]: Math.min(v[b.key] + 4, list.length) }))}
                        className="inline-flex items-center gap-1 font-medium text-[#FF7819] hover:underline">
                        Mostra altri {Math.min(4, list.length - shown)} <ChevronDown className="w-4 h-4" />
                      </button>
                    )}
                    {shown > 4 && (
                      <button data-testid={`reduce-${b.key}`} onClick={() => setVisible(v => ({ ...v, [b.key]: 4 }))}
                        className="inline-flex items-center gap-1 font-medium text-[#737373] hover:underline">
                        Riduci <ChevronUp className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                )}
              </section>
            );
          })}
          {Object.values(grouped).every(l => l.length === 0) && (
            <p className="text-center text-[#A3A3A3] py-12">Nessuna opportunità attiva con i filtri correnti.</p>
          )}
        </div>
      ) : (
        <div>
          <p className="text-sm text-[#A3A3A3] mb-3">{flatList.length} opportunità</p>
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="flat-list">
            {flatList.map(o => <OpportunityCard key={o.lead_id} o={o} onOpen={(id) => navigate(`/leads/${id}`)} />)}
          </div>
          {flatList.length === 0 && <p className="text-center text-[#A3A3A3] py-12">Nessuna opportunità con i filtri correnti.</p>}
        </div>
      )}
    </div>
  );
};

export default EstimatesPage;
